import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Layout } from "../components/Layout";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  PlusIcon,
  PencilSimpleIcon,
  TrashIcon,
  ListBulletsIcon,
  PaperPlaneTiltIcon,
  ChatCircleDotsIcon,
  XIcon,
  PlugIcon,
} from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Link } from "react-router-dom";

const empty = {
  connection_id: "",
  keyword: "",
  match_type: "contains",
  public_replies: [""],
  private_dms: [""],
  enabled: true,
  applies_to: "all",
  media_ids: [],
  triggers: ["comments"],
};

const MAX_VARIANTS = 3;

export default function Keywords() {
  const [items, setItems] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [filterConn, setFilterConn] = useState("");
  const [mediaList, setMediaList] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaError, setMediaError] = useState(null);
  const [mediaForConn, setMediaForConn] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [kw, cs] = await Promise.all([
        api.listKeywords(filterConn || undefined),
        api.listConnections(),
      ]);
      setItems(kw);
      setConnections(cs);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load keywords");
    } finally {
      setLoading(false);
    }
  }, [filterConn]);

  useEffect(() => { reload(); }, [reload]);

  // Load IG media for the form's selected connection (only when "Specific posts" is chosen)
  useEffect(() => {
    if (!open || form.applies_to !== "specific" || !form.connection_id) return;
    if (mediaForConn === form.connection_id) return;
    setMediaLoading(true);
    setMediaError(null);
    setMediaList([]);
    api
      .listMedia(form.connection_id, 25)
      .then((res) => {
        setMediaList(res.media || []);
        setMediaForConn(form.connection_id);
      })
      .catch((err) => setMediaError(err?.response?.data?.detail || "Failed to load posts"))
      .finally(() => setMediaLoading(false));
  }, [open, form.applies_to, form.connection_id, mediaForConn]);

  const connMap = useMemo(
    () => Object.fromEntries(connections.map((c) => [c.id, c])),
    [connections]
  );

  const onOpenNew = () => {
    setEditing(null);
    setForm({
      ...empty,
      connection_id: connections[0]?.id || "",
    });
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.connection_id) {
      toast.error("Pick an Instagram connection");
      return;
    }
    if (!form.keyword.trim()) {
      toast.error("Keyword is required");
      return;
    }
    const replies = form.public_replies.map((s) => s.trim()).filter(Boolean);
    const dms = form.private_dms.map((s) => s.trim()).filter(Boolean);
    if (!replies.length || !dms.length) {
      toast.error("At least one public reply and one private DM are required");
      return;
    }
    if (!form.triggers.length) {
      toast.error("Select at least one trigger source");
      return;
    }
    const payload = { ...form, public_replies: replies, private_dms: dms };
    try {
      if (editing) {
        await api.updateKeyword(editing.id, payload);
        toast.success("Keyword updated");
      } else {
        await api.createKeyword(payload);
        toast.success("Keyword added");
      }
      setOpen(false);
      setForm(empty);
      setEditing(null);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save keyword");
    }
  };

  const onEdit = (kw) => {
    setEditing(kw);
    setForm({
      connection_id: kw.connection_id,
      keyword: kw.keyword,
      match_type: kw.match_type,
      public_replies: kw.public_replies?.length ? kw.public_replies : [""],
      private_dms: kw.private_dms?.length ? kw.private_dms : [""],
      enabled: kw.enabled,
      applies_to: kw.applies_to || "all",
      media_ids: kw.media_ids || [],
      triggers: kw.triggers?.length ? kw.triggers : ["comments"],
    });
    setOpen(true);
  };

  const onDelete = async (kw) => {
    if (!window.confirm(`Delete "${kw.keyword}"?`)) return;
    await api.deleteKeyword(kw.id);
    toast.success("Keyword deleted");
    reload();
  };

  const toggleEnabled = async (kw) => {
    await api.updateKeyword(kw.id, { enabled: !kw.enabled });
    reload();
  };

  const updateVariant = (key, idx, value) => {
    const next = [...form[key]];
    next[idx] = value;
    setForm({ ...form, [key]: next });
  };
  const addVariant = (key) => {
    if (form[key].length >= MAX_VARIANTS) return;
    setForm({ ...form, [key]: [...form[key], ""] });
  };
  const removeVariant = (key, idx) => {
    const next = form[key].filter((_, i) => i !== idx);
    setForm({ ...form, [key]: next.length ? next : [""] });
  };

  const noConnections = !loading && connections.length === 0;

  return (
    <Layout
      title="Keywords"
      subtitle="Configure trigger keywords with up to 3 reply variants and 3 DM variants per keyword. Megatron rotates them randomly to avoid Instagram spam heuristics."
      action={
        !noConnections && (
          <button
            onClick={onOpenNew}
            data-testid="add-keyword-btn"
            className="inline-flex items-center justify-center gap-2 rounded-sm bg-amber-500 px-6 py-2.5 text-sm font-bold text-zinc-950 shadow-sm transition-all duration-150 hover:-translate-y-0.5 hover:bg-amber-600"
          >
            <PlusIcon size={16} weight="bold" /> New Keyword
          </button>
        )
      }
    >
      {connections.length > 0 && (
        <div className="mb-6 flex items-center gap-3">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
            Filter by connection
          </p>
          <select
            value={filterConn}
            onChange={(e) => setFilterConn(e.target.value)}
            data-testid="filter-connection"
            className="rounded-sm border border-zinc-200 bg-white px-3 py-1.5 text-sm font-mono"
          >
            <option value="">All accounts</option>
            {connections.map((c) => (
              <option key={c.id} value={c.id}>@{c.ig_username || c.fb_page_name}</option>
            ))}
          </select>
        </div>
      )}

      {(() => {
        if (loading) {
          return (
            <div className="rounded-sm border border-zinc-200 bg-white p-12 text-center text-zinc-400">
              Loading…
            </div>
          );
        }
        if (noConnections) {
          return (
            <div className="flex flex-col items-center justify-center rounded-sm border border-dashed border-zinc-300 bg-white py-24" data-testid="no-connections-empty">
              <PlugIcon size={64} weight="duotone" className="text-zinc-300" />
              <h3 className="mt-4 font-display text-2xl font-bold text-zinc-900">
                Connect an Instagram account first
              </h3>
              <p className="mt-2 max-w-md text-center text-sm text-zinc-500">
                Keywords belong to a specific IG account. Head to Connections to enable
                automation on one of your Facebook Pages.
              </p>
              <Link
                to="/connections"
                className="mt-6 inline-flex items-center gap-2 rounded-sm bg-amber-500 px-6 py-2.5 text-sm font-bold text-zinc-950 hover:bg-amber-600"
                data-testid="go-to-connections"
              >
                <PlugIcon size={16} weight="bold" /> Go to Connections
              </Link>
            </div>
          );
        }
        if (items.length === 0) {
          return (
            <div className="flex flex-col items-center justify-center rounded-sm border border-dashed border-zinc-300 bg-white py-24">
              <ListBulletsIcon size={64} weight="duotone" className="text-zinc-300" />
              <h3 className="mt-4 font-display text-2xl font-bold text-zinc-900">
                No keywords yet
              </h3>
              <p className="mt-2 max-w-md text-center text-sm text-zinc-500">
                Create your first trigger. When someone comments with this keyword on
                your Instagram post, Megatron will reply publicly and slide into their DMs.
              </p>
              <button
                onClick={onOpenNew}
                data-testid="empty-add-keyword-btn"
                className="mt-6 inline-flex items-center gap-2 rounded-sm bg-amber-500 px-6 py-2.5 text-sm font-bold text-zinc-950 hover:bg-amber-600"
              >
                <PlusIcon size={16} weight="bold" /> Create Keyword
              </button>
            </div>
          );
        }
        return (
          <div className="overflow-x-auto rounded-sm border border-zinc-200 bg-white" data-testid="keywords-table">
            <table className="w-full min-w-[800px] text-sm">
              <thead className="border-b border-zinc-200 bg-zinc-50">
                <tr className="text-left">
                  <th className="p-4 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Keyword</th>
                  <th className="p-4 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Account</th>
                  <th className="p-4 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Match</th>
                  <th className="p-4 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Variants</th>
                  <th className="p-4 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Triggers</th>
                  <th className="p-4 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Status</th>
                  <th className="p-4 text-right text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((kw) => {
                  const conn = connMap[kw.connection_id];
                  return (
                    <tr key={kw.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`keyword-row-${kw.id}`}>
                      <td className="p-4">
                        <span className="rounded-sm bg-amber-100 px-2 py-1 font-mono text-xs font-bold uppercase text-amber-900">
                          {kw.keyword}
                        </span>
                      </td>
                      <td className="p-4 font-mono text-xs text-zinc-700">
                        @{conn?.ig_username || "unknown"}
                      </td>
                      <td className="p-4 font-mono text-xs uppercase text-zinc-600">{kw.match_type}</td>
                      <td className="p-4 text-zinc-700">
                        <span className="inline-flex items-center gap-1 mr-2" title={(kw.public_replies || []).join(" • ")}>
                          <PaperPlaneTiltIcon size={14} weight="duotone" className="text-amber-500" />
                          {(kw.public_replies || []).length}
                        </span>
                        <span className="inline-flex items-center gap-1" title={(kw.private_dms || []).join(" • ")}>
                          <ChatCircleDotsIcon size={14} weight="duotone" className="text-emerald-500" />
                          {(kw.private_dms || []).length}
                        </span>
                      </td>
                      <td className="p-4">
                        {(kw.triggers || []).map((t) => (
                          <span key={t} className="mr-1 rounded-sm bg-zinc-100 px-1.5 py-0.5 font-mono text-[10px] uppercase text-zinc-700">
                            {t}
                          </span>
                        ))}
                      </td>
                      <td className="p-4">
                        <Switch checked={kw.enabled} onCheckedChange={() => toggleEnabled(kw)} data-testid={`toggle-${kw.id}`} />
                      </td>
                      <td className="p-4">
                        <div className="flex justify-end gap-2">
                          <button onClick={() => onEdit(kw)} data-testid={`edit-${kw.id}`}
                            className="rounded-sm p-2 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900">
                            <PencilSimpleIcon size={16} />
                          </button>
                          <button onClick={() => onDelete(kw)} data-testid={`delete-${kw.id}`}
                            className="rounded-sm p-2 text-zinc-500 hover:bg-red-50 hover:text-red-600">
                            <TrashIcon size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })()}

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
        <DialogContent className="rounded-sm sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-bold tracking-tight">
              {editing ? "Edit Keyword" : "New Keyword"}
            </DialogTitle>
            <DialogDescription>
              Up to 3 variants for both reply and DM. Megatron picks one randomly per match.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                Instagram account
              </label>
              <select
                value={form.connection_id}
                onChange={(e) => setForm({ ...form, connection_id: e.target.value })}
                className="input-megatron mt-1 font-mono"
                data-testid="form-connection"
              >
                <option value="">Pick a connection…</option>
                {connections.map((c) => (
                  <option key={c.id} value={c.id}>
                    @{c.ig_username || c.fb_page_name} ({c.fb_page_name})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Keyword</label>
              <input
                value={form.keyword}
                onChange={(e) => setForm({ ...form, keyword: e.target.value })}
                placeholder="YOGA"
                className="input-megatron mt-1 font-mono uppercase"
                data-testid="form-keyword"
              />
            </div>

            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Match type</label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {["contains", "exact"].map((t) => (
                  <button key={t} type="button" onClick={() => setForm({ ...form, match_type: t })}
                    data-testid={`match-${t}`}
                    className={`rounded-sm border px-3 py-2 text-sm font-semibold uppercase tracking-wide ${
                      form.match_type === t
                        ? "border-amber-500 bg-amber-50 text-amber-900"
                        : "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <VariantList
              label="Public reply variants"
              icon={PaperPlaneTiltIcon}
              accent="text-amber-500"
              values={form.public_replies}
              onChange={(i, v) => updateVariant("public_replies", i, v)}
              onAdd={() => addVariant("public_replies")}
              onRemove={(i) => removeVariant("public_replies", i)}
              testidPrefix="public-reply"
              placeholder="Sent you a DM!"
            />

            <VariantList
              label="Private DM variants"
              icon={ChatCircleDotsIcon}
              accent="text-emerald-500"
              values={form.private_dms}
              onChange={(i, v) => updateVariant("private_dms", i, v)}
              onAdd={() => addVariant("private_dms")}
              onRemove={(i) => removeVariant("private_dms", i)}
              testidPrefix="private-dm"
              placeholder="Here's your link: https://…"
              rows={2}
            />

            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Trigger on</label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {[
                  { v: "comments", label: "Post Comments" },
                  { v: "dms", label: "DMs & Story Replies" },
                ].map((o) => {
                  const active = form.triggers.includes(o.v);
                  return (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => {
                        const next = active ? form.triggers.filter((t) => t !== o.v) : [...form.triggers, o.v];
                        setForm({ ...form, triggers: next });
                      }}
                      data-testid={`trigger-${o.v}`}
                      className={`rounded-sm border px-3 py-2 text-sm font-semibold uppercase tracking-wide ${
                        active
                          ? "border-amber-500 bg-amber-50 text-amber-900"
                          : "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50"
                      }`}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Applies to</label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {[
                  { v: "all", label: "All Posts" },
                  { v: "specific", label: "Specific Posts" },
                ].map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setForm({ ...form, applies_to: o.v })}
                    data-testid={`applies-${o.v}`}
                    className={`rounded-sm border px-3 py-2 text-sm font-semibold uppercase tracking-wide ${
                      form.applies_to === o.v
                        ? "border-amber-500 bg-amber-50 text-amber-900"
                        : "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              {form.applies_to === "specific" && (
                <div className="mt-3">
                  {mediaLoading ? (
                    <p className="text-xs text-zinc-500">Loading posts…</p>
                  ) : mediaError ? (
                    <p className="rounded-sm border border-red-200 bg-red-50 p-2 text-xs text-red-700" data-testid="kw-media-error">
                      {mediaError}
                    </p>
                  ) : mediaList.length === 0 ? (
                    <p className="text-xs text-zinc-500">No posts available for this account.</p>
                  ) : (
                    <>
                      <p className="mb-2 text-xs text-zinc-500">
                        Select the posts this keyword should monitor ({form.media_ids.length} selected).
                      </p>
                      <div className="grid max-h-56 grid-cols-4 gap-2 overflow-y-auto rounded-sm border border-zinc-200 bg-zinc-50 p-2">
                        {mediaList.map((m) => {
                          const selected = form.media_ids.includes(m.id);
                          const thumb = m.thumbnail_url || m.media_url;
                          return (
                            <button
                              key={m.id}
                              type="button"
                              onClick={() => {
                                const ids = selected
                                  ? form.media_ids.filter((id) => id !== m.id)
                                  : [...form.media_ids, m.id];
                                setForm({ ...form, media_ids: ids });
                              }}
                              data-testid={`media-select-${m.id}`}
                              className={`relative aspect-square overflow-hidden rounded-sm border-2 transition-all ${
                                selected
                                  ? "border-amber-500 ring-2 ring-amber-300"
                                  : "border-zinc-200 hover:border-zinc-400"
                              }`}
                              title={(m.caption || "").slice(0, 80)}
                            >
                              {thumb ? (
                                <img src={thumb} alt="" className="h-full w-full object-cover" />
                              ) : (
                                <div className="flex h-full w-full items-center justify-center bg-zinc-100 text-[10px] text-zinc-400">
                                  {m.media_type}
                                </div>
                              )}
                              {selected && (
                                <span className="absolute right-1 top-1 rounded-full bg-amber-500 px-1.5 py-0.5 text-[8px] font-bold uppercase text-zinc-950">✓</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between rounded-sm border border-zinc-200 bg-zinc-50 px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-zinc-900">Enabled</p>
                <p className="text-xs text-zinc-500">Listen for this keyword on the selected account.</p>
              </div>
              <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="form-enabled" />
            </div>

            <DialogFooter>
              <button type="button" onClick={() => setOpen(false)}
                className="rounded-sm border border-zinc-200 bg-white px-6 py-2.5 text-sm font-semibold text-zinc-900 hover:bg-zinc-50">
                Cancel
              </button>
              <button type="submit" data-testid="form-submit"
                className="rounded-sm bg-amber-500 px-6 py-2.5 text-sm font-bold text-zinc-950 hover:bg-amber-600">
                {editing ? "Save changes" : "Create keyword"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

const VariantList = ({ label, icon: Icon, accent, values, onChange, onAdd, onRemove, testidPrefix, placeholder, rows = 1 }) => (
  <div>
    <div className="flex items-center justify-between">
      <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1">
        <Icon size={12} weight="duotone" className={accent} /> {label}
      </label>
      <span className="text-[10px] text-zinc-400">{values.length}/{MAX_VARIANTS}</span>
    </div>
    <div className="mt-1 space-y-2">
      {values.map((v, i) => (
        <div key={i} className="flex items-start gap-2">
          <textarea
            value={v}
            onChange={(e) => onChange(i, e.target.value)}
            rows={rows}
            placeholder={`${placeholder} (variant ${i + 1})`}
            className="input-megatron flex-1"
            data-testid={`form-${testidPrefix}-${i}`}
          />
          {values.length > 1 && (
            <button type="button" onClick={() => onRemove(i)}
              data-testid={`form-${testidPrefix}-remove-${i}`}
              className="rounded-sm border border-zinc-200 bg-white p-2 text-zinc-500 hover:bg-red-50 hover:text-red-600">
              <XIcon size={14} />
            </button>
          )}
        </div>
      ))}
      {values.length < MAX_VARIANTS && (
        <button type="button" onClick={onAdd}
          data-testid={`form-${testidPrefix}-add`}
          className="inline-flex items-center gap-1 rounded-sm border border-dashed border-zinc-300 bg-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-zinc-600 hover:border-amber-400 hover:text-amber-700">
          <PlusIcon size={12} weight="bold" /> Add variant
        </button>
      )}
    </div>
  </div>
);
